import { FC } from 'react';
import { graphql, useFragment, useRelayEnvironment, commitLocalUpdate } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';
import { usePaymentMethodSelectContext } from '../PaymentMethodSelectContext';
import { type PaymentMethod } from '../../types';
import { ConfirmModal, useConfirmModalProps } from '../confirm/ConfirmModal';

import { useDeleteWalletPaymentMethodMutation } from './deleteWalletPaymentMethod';
import { deleteWalletPaymentMethodUpdater } from './deleteWalletPaymentMethodUpdater';
import { WalletCreditCardOption } from './WalletCreditCardOption';
import { WalletAchOption } from './WalletAchOption';

import { WalletPaymentMethod_buyer$key } from './__generated__/WalletPaymentMethod_buyer.graphql';
import { WalletPaymentMethod_viewer$key } from './__generated__/WalletPaymentMethod_viewer.graphql';

const buyerFragment = graphql`
    fragment WalletPaymentMethod_buyer on User {
        serviceId
        wallet {
            paymentMethods {
                currencies
                paymentMethod {
                    paymentMethodType
                    ... on CreditCard {
                        token
                        hashedCardNumber
                        paymentProcessor
                        billingAddress {
                            firstName
                            lastName
                            line1
                            line2
                            country
                            zipCode
                            company
                            state
                            city
                        }
                        ...WalletCreditCardOption_creditCard
                    }
                    ... on ACH {
                        token
                        paymentProcessor
                        ...WalletAchOption_ach
                    }
                }
                shippingAddresses {
                    serviceId
                }
            }
        }
        preferences {
            currency
        }
        isUsingSupportedChargeCurrency
    }
`;

const viewerFragment = graphql`
    fragment WalletPaymentMethod_viewer on Viewer {
        id
        ...WalletCreditCardOption_viewer
    }
`;

type Props = {
    buyer: WalletPaymentMethod_buyer$key | null;
    viewer: WalletPaymentMethod_viewer$key;
};

export const WalletPaymentMethod: FC<Props> = props => {
    const { buyer: buyerRef, viewer: viewerRef } = props;
    const relayEnvironment = useRelayEnvironment();
    const {
        selectedPaymentMethod,
        updateSelectedPaymentMethod,
        paymentMethods,
        shippingAddressId,
        mockWallet,
        billingAddress: billingAddressFromContext,
    } = usePaymentMethodSelectContext();

    const buyer = useFragment(buyerFragment, buyerRef);
    const viewer = useFragment(viewerFragment, viewerRef);
    const buyerCurrency = buyer?.isUsingSupportedChargeCurrency
        ? buyer?.preferences?.currency
        : 'USD';
    const [commitDeleteMutation, isDeletingPaymentMethod] = useDeleteWalletPaymentMethodMutation();
    const [modalProps, promptConfirmation, closeConfirmationModal] = useConfirmModalProps();

    if (!buyer) {
        return null;
    }

    const walletPaymentMethods =
        buyer.wallet?.paymentMethods?.filter(walletPaymentMethod => {
            const method = walletPaymentMethod?.paymentMethod;
            return (
                paymentMethods.some(
                    option =>
                        method?.paymentMethodType === option?.paymentMethodType &&
                        method?.paymentProcessor === option.paymentProcessor
                ) && walletPaymentMethod?.currencies?.includes(buyerCurrency || null)
            );
        }) || [];

    function handleWalletMethodSelect(paymentMethod: PaymentMethod): void {
        updateSelectedPaymentMethod(paymentMethod);
    }

    async function handlePaymentMethodDelete(paymentMethod: PaymentMethod): Promise<void> {
        const buyerId = buyer?.serviceId;
        if (!buyerId || !paymentMethod.paymentMethodToken) {
            return;
        }

        const { paymentMethodType, paymentProcessor, hashedCardNumber, paymentMethodToken } =
            paymentMethod || {};

        const paymentMethodKey =
            paymentMethodType === 'CREDIT_CARD' ? hashedCardNumber : paymentMethodToken;

        const isConfirmed = await promptConfirmation();
        if (!isConfirmed) {
            return;
        }

        const wasSelected =
            !!selectedPaymentMethod &&
            selectedPaymentMethod.paymentMethodType === paymentMethodType &&
            selectedPaymentMethod.paymentMethodToken === paymentMethodToken &&
            selectedPaymentMethod.paymentProcessor === paymentProcessor;

        // try to find another payment method of this type
        const newOption = walletPaymentMethods?.find(walletPaymentMethod => {
            const method = walletPaymentMethod?.paymentMethod;
            return (
                method?.paymentMethodType === paymentMethodType &&
                method?.paymentProcessor === paymentProcessor &&
                method?.token !== paymentMethodToken
            );
        })?.paymentMethod;

        const newOptionToken = newOption?.token;
        const newOptionBillingAddress = newOption?.billingAddress;
        const { firstName, lastName, line1, line2, country, zipCode, company, state, city } =
            newOptionBillingAddress || {};

        function onCompleted(): void {
            if (wasSelected || !newOptionToken) {
                updateSelectedPaymentMethod({
                    paymentMethodType,
                    paymentProcessor,
                    paymentMethodToken: newOptionToken || undefined,
                    //when there are no more saved credit cards, we open the new card form. Billing address should be set to the selected shipping address from context. Otherwise, we use the billing address from the newly selected saved payment method
                    billingAddress: wasSelected
                        ? {
                              firstName,
                              lastName,
                              line1,
                              line2,
                              country,
                              zipCode,
                              company,
                              state,
                              city,
                          }
                        : billingAddressFromContext,
                });
            }
            closeConfirmationModal();
        }

        if (mockWallet) {
            commitLocalUpdate(relayEnvironment, store =>
                deleteWalletPaymentMethodUpdater(store, viewer.id, buyerId, paymentMethodToken)
            );
            onCompleted();
        } else {
            commitDeleteMutation({
                variables: {
                    input: {
                        userId: buyerId,
                        paymentMethodKey: paymentMethodKey || '',
                        paymentMethodType: paymentMethod.paymentMethodType,
                    },
                },
                optimisticUpdater: store =>
                    deleteWalletPaymentMethodUpdater(store, viewer.id, buyerId, paymentMethodToken),
            });
            onCompleted();
        }
    }

    return (
        <>
            {walletPaymentMethods.map(walletPaymentMethod => {
                const paymentMethod = walletPaymentMethod?.paymentMethod;

                if (!paymentMethod) {
                    return null;
                }

                const { paymentMethodType, token, paymentProcessor } = paymentMethod;

                const isSelected =
                    !!selectedPaymentMethod &&
                    selectedPaymentMethod.paymentMethodType === paymentMethodType &&
                    selectedPaymentMethod.paymentMethodToken === token &&
                    selectedPaymentMethod.paymentProcessor === paymentProcessor;

                const isPermissible =
                    !!mockWallet ||
                    !shippingAddressId ||
                    !!walletPaymentMethod?.shippingAddresses?.some(
                        address => address?.serviceId === shippingAddressId
                    );

                switch (paymentMethodType) {
                    case 'CREDIT_CARD': {
                        return (
                            <WalletCreditCardOption
                                key={token}
                                creditCard={paymentMethod}
                                viewer={viewer}
                                isChecked={isSelected}
                                name="wallet-payment-method"
                                dataTn={`payment-method-credit-card-${token}`}
                                isPermissible={isPermissible}
                                onChange={handleWalletMethodSelect}
                                onDelete={handlePaymentMethodDelete}
                            />
                        );
                    }
                    case 'ACH': {
                        return (
                            <WalletAchOption
                                key={token}
                                ach={paymentMethod}
                                isChecked={isSelected}
                                name="wallet-payment-method"
                                dataTn={`payment-method-ach-${token}`}
                                isPermissible={isPermissible}
                                onChange={handleWalletMethodSelect}
                                onDelete={handlePaymentMethodDelete}
                            />
                        );
                    }
                    default:
                        return null;
                }
            })}
            <ConfirmModal
                {...modalProps}
                title={
                    <FormattedMessage
                        id="dibs-payment.WalletPaymentMethod.deletingPaymentTitle"
                        defaultMessage="Delete Payment Method?"
                    />
                }
                confirmLabel={
                    <FormattedMessage
                        id="dibs-payment.WalletPaymentMethod.confirmLabel"
                        defaultMessage="Delete"
                    />
                }
                isBusy={isDeletingPaymentMethod}
            >
                <FormattedMessage
                    id="dibs-payment.WalletPaymentMethod.deletingPaymentContent"
                    defaultMessage="Permanently delete this payment method?"
                />
            </ConfirmModal>
        </>
    );
};
